/**
 * Thin Drive auth API client (SSO exchange / session / status only).
 * Product API calls stay on the existing axios stack.
 */

import {
  beginDriveReauth,
  DRIVE_APP_SLUG,
  getDriveApiToken,
  isAuthSessionExpiredResponse
} from './auth-token';

function resolveDriveAuthBaseUrl(override?: string): string {
  const raw =
    override?.trim() ||
    (typeof process !== 'undefined' &&
      process.env.REACT_APP_DRIVE_API_URL?.trim()) ||
    (typeof process !== 'undefined' &&
      process.env.REACT_APP_API_URL?.trim()) ||
    'https://api.drive.cynayd.com';

  // Auth routes live on the Drive API host root (/auth/...), not under /api.
  try {
    const u = new URL(raw);
    // If env points at .../api or .../api/, strip that path for auth calls.
    if (u.pathname === '/api' || u.pathname === '/api/') {
      u.pathname = '';
    }
    return u.origin + (u.pathname === '/' ? '' : u.pathname.replace(/\/$/, ''));
  } catch {
    return raw.replace(/\/api\/?$/, '').replace(/\/$/, '');
  }
}

/** Paths that must never trigger One re-auth redirect (bootstrap/exchange). */
export const AUTH_EXEMPT_PATHS = [
  '/auth/status',
  '/auth/sso/exchange',
  '/auth/session',
  '/auth/debug/jwt',
  '/health'
];

export class DriveAuthApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(
      typeof body === 'object' && body && 'message' in body
        ? Array.isArray((body as { message: unknown }).message)
          ? String((body as { message: unknown[] }).message[0])
          : typeof (body as { message: unknown }).message === 'object' &&
              (body as { message: { message?: string } }).message?.message
            ? String(
                (body as { message: { message: string } }).message.message
              )
            : String((body as { message: unknown }).message)
        : `HTTP ${status}`
    );
    this.status = status;
    this.body = body;
  }
}

export function isAuthExemptPath(path: string): boolean {
  const normalized = path.startsWith('http')
    ? (() => {
        try {
          return new URL(path).pathname;
        } catch {
          return path;
        }
      })()
    : path;
  return AUTH_EXEMPT_PATHS.some(
    (p) =>
      normalized === p ||
      normalized.startsWith(`${p}?`) ||
      normalized.startsWith(`${p}/`) ||
      normalized.endsWith(p) ||
      normalized.includes(`${p}?`)
  );
}

let reauthSingleFlight: Promise<void> | null = null;

/**
 * Shared 401→One resume for axios + fetch. Does not redirect on 403/404/5xx.
 * Does not auto-retry mutating requests.
 */
export function maybeBeginDriveReauthOn401(options: {
  path: string;
  status: number;
  body: unknown;
  hadBearer: boolean;
}): void {
  const { path, status, body, hadBearer } = options;
  if (status === 403 || status === 404 || status >= 500) return;
  if (isAuthExemptPath(path)) return;
  if (!hadBearer) return;
  if (!isAuthSessionExpiredResponse(status, body)) return;
  if (reauthSingleFlight) return;
  reauthSingleFlight = Promise.resolve().then(() => {
    const ok = beginDriveReauth({ reason: 'API_401' });
    if (!ok) {
      reauthSingleFlight = null;
    }
  });
}

async function request<T>(
  path: string,
  init?: RequestInit,
  apiBaseUrl?: string
): Promise<T> {
  const base = resolveDriveAuthBaseUrl(apiBaseUrl);
  const token = getDriveApiToken();
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      ...(init?.body instanceof FormData
        ? {}
        : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {})
    }
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    maybeBeginDriveReauthOn401({
      path,
      status: res.status,
      body: json,
      hadBearer: Boolean(token)
    });
    throw new DriveAuthApiError(res.status, json);
  }

  // Support both { data: T } envelopes and bare T
  if (json && typeof json === 'object' && 'data' in json && json.data != null) {
    return json.data as T;
  }
  return json as T;
}

export type DriveAuthClient = {
  authStatus: () => Promise<{
    authRequired: boolean;
    appSlug: string;
    ssoConfigured: boolean;
    accessOpsConfigured?: boolean;
    launchRevalidate?: boolean;
  }>;
  exchangeSsoCredential: (opts: {
    code?: string;
    token?: string;
    appSlug?: string;
  }) => Promise<{
    apiToken: string;
    user: unknown;
    accessibleApps?: unknown;
  }>;
  authSession: () => Promise<{
    ok: boolean;
    userId: string;
    tenantId: string;
    email: string | null;
    authRequired: boolean;
  }>;
};

export function createDriveAuthClient(apiBaseUrl?: string): DriveAuthClient {
  return {
    authStatus: () =>
      request(
        '/auth/status',
        undefined,
        apiBaseUrl
      ),
    exchangeSsoCredential: (opts) =>
      request(
        '/auth/sso/exchange',
        {
          method: 'POST',
          body: JSON.stringify({
            ...opts,
            appSlug: opts.appSlug || DRIVE_APP_SLUG
          })
        },
        apiBaseUrl
      ),
    authSession: () =>
      request(
        '/auth/session',
        undefined,
        apiBaseUrl
      )
  };
}

/** Default client using env base URL. */
export const driveAuthApi = createDriveAuthClient();

export function getDriveAuthBaseUrl(override?: string): string {
  return resolveDriveAuthBaseUrl(override);
}
